import React from 'react';
import styled from 'styled-components';
import Popup from '../Popup';
import { Card, ModalSheetTitle, Subtitle } from '../../styledhtml/Tailwind';
import Constants from '../../submodules/logictry_config/constants';
import Navigation from '../../services/Navigation';
import UserAccount from '../../services/UserAccount';
import LogicBaseUserCache from '../../services/cache/LogicBaseUserCache';
import LogicBaseCollectionCache from '../../services/cache/LogicBaseCollectionCache';
import LogicBaseConversationCache from '../../services/cache/LogicBaseConversationCache';
import LogicBaseCommentsCache from '../../services/cache/LogicBaseCommentCache';
import UserCache from '../../services/cache/UserCache';
import LogicBaseRelationshipCache from '../../services/cache/LogicBaseRelationshipCache';
import LogicBaseNotificationCache from '../../services/cache/LogicBaseNotificationCache';
import TreeCache from '../../services/cache/TreeCache';
import { getNotifications } from '../../services/Pagination';
import timeAgo from '../../utils/timeAgo';
import Scrollable from '../../components/Scrollable';
import LoadingIndicator from '../LoadingIndicator';
import UserProfileHeader from '../../components/UserProfileHeader';
import getFormattedTextElements from '../../utils/getFormattedTextElements';
import { AngleRightIcon, BellIcon, UsersIcon } from '../../styledhtml/Icon';
import LogictryComponent from '../../submodules/logictry_config/LogictryComponent';
import LogicBaseNotificationPopupService from '../../services/LogicBaseNotificationPopup';
const EmbeddedShell = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
`;

const EmbeddedHeader = styled.div`
  flex-shrink: 0;
  padding: 1.25rem 1.25rem 0.85rem;
  border-bottom: ${Constants.BottomBorderDivider};
  background: ${Constants.SurfaceColor};
`;

const NotificationWrapper = styled.div`
  max-width: 48rem;
  margin: auto;
  display: flex;
  flex-direction: column;

  h1 {
    margin: 2rem 0;
    text-align: center;
  }
`;

const NotificationList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

/** Aligned with LogicBaseSearchBar result rows: border card, CTA shadow on hover. */
const NotificationItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 1rem;
  border-radius: ${Constants.BoxRadius}px;
  background: ${(p) => (p.$unread ? `color-mix(in srgb, ${Constants.PrimaryColor} 7%, transparent)` : Constants.SurfaceColor)};
  cursor: pointer;
  text-align: left;
  border: 1px solid ${(p) => (p.$unread ? `color-mix(in srgb, ${Constants.PrimaryColor} 33%, transparent)` : Constants.LightBorderColor)};
  border-left-width: ${(p) => (p.$unread ? '4px' : '1px')};
  border-left-color: ${(p) => (p.$unread ? Constants.PrimaryColor : Constants.LightBorderColor)};
  transition: box-shadow 0.15s ease, border-color 0.15s ease, background 0.15s ease;

  &:hover {
    box-shadow: ${Constants.CTAShadow};
  }
`;

const ItemLeading = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const DefaultAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${Constants.SurfaceHover};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  > * {
    ${Constants.isDarkMode ? 'filter: invert(1);' : ''}
  }
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const ItemMessage = styled.div`
  font-size: ${Constants.SemiSmallFontSize};
  line-height: 1.35;
  color: ${Constants.DarkTextColor};
  font-weight: ${(p) => (p.$unread ? 600 : 400)};
`;

const ItemTime = styled.div`
  font-size: ${Constants.VerySmallFontSize};
  color: ${Constants.MediumTextGray};
`;

const ItemChevron = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  opacity: 0.35;
`;

const CalloutCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;
  padding: 0.55rem 0.75rem;
  border-radius: ${Constants.BoxRadius}px;
  cursor: pointer;
  font-size: ${Constants.SemiSmallFontSize};
  font-weight: 500;
  border: ${Constants.LightBorder};
  transition: background 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    box-shadow: ${Constants.CTAShadow};
  }
`;

const CalloutWarning = styled(CalloutCard)`
  background: ${Constants.LightBackground};
  border-color: #f5c6cb;
  color: #c0392b;

  &:hover {
    background: ${Constants.isDarkMode ? 'rgba(192, 57, 43, 0.16)' : '#fff5f5'};
  }
`;

const CalloutNeutral = styled(CalloutCard)`
  background: ${Constants.StaticGray};
  color: ${Constants.DarkTextColor};

  &:hover {
    background: ${Constants.MediumBackground};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1.25rem 1.5rem;
  gap: 0.75rem;
`;

const EmptyIconCircle = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${Constants.SurfaceHover};
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.85;
  > * {
    ${Constants.isDarkMode ? 'filter: invert(1);' : ''}
  }
`;

const GET_APP_TYPES = ['NewAppLike', 'NewAppPin', 'NewAppEditor', 'NewAppOwner', 'NewComment'];
const GET_COLLECTION_TYPES = ['NewCollectionSubscriber', 'NewCollectionEditor', 'NewCollectionOwner', 'NewAppCollection', 'NewCollectionComment'];
const GET_COMMENT_TYPES = ['NewCommentMention', 'NewCommentReply', 'NewCommentLike', 'NewCollectionCommentMention', 'NewCollectionCommentReply', 'NewCollectionCommentLike'];
const GET_USER_TYPES = ['NewFollower', 'FollowApproved', 'NewFollowRequest'];
const GET_CONVERSATION_TYPES = ['NewMessage'];

export default class LogicBaseNotificationPopup extends LogictryComponent {
  constructor() {
    super();
    this.notifications = getNotifications();
  }
  state = {}
  componentDidMount() {
    LogicBaseNotificationPopupService.onStateUpdate(this);
    LogicBaseUserCache.onStateUpdate(this);
    UserCache.onStateUpdate(this);
    this.notifications.onStateUpdate(this);
    UserAccount.onStateUpdate(this);
    TreeCache.onStateUpdate(this);
    LogicBaseRelationshipCache.onStateUpdate(this);
    LogicBaseCollectionCache.onStateUpdate(this);
    LogicBaseConversationCache.onStateUpdate(this);
    LogicBaseNotificationCache.onStateUpdate(this);
    LogicBaseCommentsCache.onStateUpdate(this);
  }
  componentWillUnmount() {
    LogicBaseNotificationPopupService.offStateUpdate(this);
    LogicBaseUserCache.offStateUpdate(this);
    UserCache.offStateUpdate(this);
    this.notifications.offStateUpdate(this);
    UserAccount.offStateUpdate(this);
    TreeCache.offStateUpdate(this);
    LogicBaseRelationshipCache.offStateUpdate(this);
    LogicBaseCollectionCache.offStateUpdate(this);
    LogicBaseConversationCache.offStateUpdate(this);
    LogicBaseNotificationCache.offStateUpdate(this);
    LogicBaseCommentsCache.offStateUpdate(this);
  }
  close = () => {
    const unread = (this.notifications.currentPage || []).filter((notification) => notification && !notification.read);
    this.markNotificationsRead(unread);
    LogicBaseNotificationPopupService.close();
  };

  /**
   * Mark the given unread notifications as read on the server exactly once each.
   */
  markNotificationsRead = (notifications) => {
    if (!notifications || !notifications.length) return;
    if (!this._markedRead) this._markedRead = new Set();
    notifications.forEach((notification) => {
      if (!notification || notification.read) return;
      const id = notification._id;
      if (!id || this._markedRead.has(id)) return;
      this._markedRead.add(id);
      notification.read = true;
      LogicBaseNotificationCache.update(notification);
    });
  }
  onPagination = () => {
    if (this.notifications.showMore && this.notifications.allFound && this.notifications.initialized) {
      this.notifications.getMore();
    }
  }
  navigateFromNotification = (url) => {
    this.close();
    Navigation.push(url);
  }

  getUserAction = (unread, user, url, text, updatedTime) => (
    <NotificationItem
      $unread={unread}
      onClick={() => this.navigateFromNotification(url)}
    >
      <ItemLeading>
        {user ? (
          <UserProfileHeader
            user={{
              _id: user._id,
              image: user.image,
              description: user.description,
              username: user.username,
              fullname: user.fullname,
            }}
            small
            hideFollow
            hideProfile
          />
        ) : (
          <DefaultAvatar aria-hidden>
            <UsersIcon style={{ width: 15, height: 15, cursor: 'default', opacity: 0.55 }} />
          </DefaultAvatar>
        )}
      </ItemLeading>
      <ItemBody>
        <ItemMessage $unread={unread}>
          {user && (user.fullname && `${user.fullname} ` || `@${user.username || `${user._id.slice(0, 8)}...`} `)}
          {text}
        </ItemMessage>
        <ItemTime>{timeAgo(new Date(updatedTime))}</ItemTime>
      </ItemBody>
      <ItemChevron aria-hidden>
        <AngleRightIcon style={{ width: 14, height: 14, flexShrink: 0 }} />
      </ItemChevron>
    </NotificationItem>
  )
  render() {
    const { isOpen } = LogicBaseNotificationPopupService;
    if (!isOpen || !UserAccount.isLoggedIn) return null;

    const { account } = UserAccount;
    const loadedPage = this.notifications.currentPage;
    const pageReady = loadedPage.length > 0 || !!this.notifications.allFound;
    if (loadedPage.length > 0) this.lastNotifications = loadedPage;
    const notifications = pageReady ? loadedPage : this.lastNotifications;

    const followRequest = (LogicBaseRelationshipCache.query({ 'fe': account._id, 'r': true, limit: 1 }) || [])[0];

    const conversationIds = notifications && notifications.map(({ objectId, type }) => (GET_CONVERSATION_TYPES.includes(type)) && objectId).filter((v) => v);
    const getConversations = conversationIds?.length > 0;
    const conversations = notifications && LogicBaseConversationCache.getByIds(Array.from(new Set([...conversationIds])))?.filter(({ _id }) => _id);

    const appIds = notifications && notifications.map(({ objectId, type }) => (GET_APP_TYPES.includes(type)) && objectId).filter((v) => v);
    const getApps = appIds && appIds.length > 0;
    const apps = notifications && TreeCache.getByIds(Array.from(new Set([...appIds])))?.filter(({ _id }) => _id);

    const commentIds = notifications && notifications.map(({ objectId, type }) => (GET_COMMENT_TYPES.includes(type)) && objectId).filter((v) => v);
    const getComments = commentIds && commentIds.length > 0;
    const comments = getComments && LogicBaseCommentsCache.getByIds(commentIds)?.filter(({ _id }) => _id);

    const collectionIds = Array.from(new Set([
      ...(notifications || []).map(({ objectId, type }) => (GET_COLLECTION_TYPES.includes(type)) && objectId).filter((v) => v)
    ]));
    const getCollections = collectionIds && collectionIds.length > 0;
    const collections = getCollections && LogicBaseCollectionCache.getByIds(collectionIds)?.filter(({ _id }) => _id);

    // Get Users
    let userIds = notifications && notifications.map(({ type, objectId }) => (GET_USER_TYPES.includes(type)) && objectId).filter((v) => v);
    if (conversations?.length > 0) userIds = [...userIds, ...conversations.map(({ participants }) => participants.find((p) => p !== account._id))];
    const getUsers = userIds && userIds.length > 0;
    const users = getUsers && UserCache.getUsersByIds(Array.from(new Set(userIds)));

    const unreadMap = {};
    const waitingForRelated = !!((getConversations && !conversations)
      || (getUsers && !users)
      || (getApps && !apps)
      || (getComments && !comments)
      || (getCollections && !collections));
    const listReady = !!(notifications && !waitingForRelated);
    let showNotifications = this._lastShowNotifications || [];
    if (listReady) {
      const newNotifications = [];
      notifications.forEach((notification) => {
        const { _id, createdTime, type, objectId, read } = notification;
        if (!read) unreadMap[_id] = true;
        const app = apps && apps.find(({ _id }) => _id === objectId);
        let user = users && users.find(({ _id }) => _id === objectId);
        const comment = comments && comments.find(({ _id }) => _id === objectId);
        const collection = collections && collections.find(({ _id }) => _id === objectId);
        const conversation = conversations && conversations.find(({ _id }) => _id === objectId);
        if (conversation) {
          user = users && users.find(({ _id }) => _id === conversation.participants.find((p) => p !== account._id));
        }
        if ((GET_CONVERSATION_TYPES.includes(type)) && (!conversation)) return;
        if ((GET_COLLECTION_TYPES.includes(type)) && (!collection)) return;
        if ((GET_APP_TYPES.includes(type)) && (!app)) return;
        if ((GET_COMMENT_TYPES.includes(type)) && (!comment)) return;
        if ((GET_USER_TYPES.includes(type)) && (!user)) return;

        newNotifications.push({ _id, objectId, type, notification, app, collection, comment, time: createdTime, user });
      });
      showNotifications = newNotifications;
      this._lastShowNotifications = newNotifications;
    }
    const showLoading = !listReady && showNotifications.length === 0;
    const scrollStyle = {
      position: 'relative',
      width: '100%',
      flex: 1,
      minHeight: 0,
      padding: 0,
    };
    const listBlock = (
      <NotificationWrapper
        style={{
          maxWidth: 'none',
          margin: 0,
          width: '100%',
          boxSizing: 'border-box',
          padding: '0.75rem 1.25rem 1.5rem',
        }}
      >
        <NotificationList>
            {(!account.username || !account.fullname || !account.image?.link || !account.password) && (
              <CalloutWarning onClick={() => this.navigateFromNotification('/account/settings')}>
                <span>Your contact information is incomplete</span>
                <AngleRightIcon style={{ width: 12, height: 12, flexShrink: 0, opacity: 0.55 }} />
              </CalloutWarning>
            )}
            {followRequest && (
              <CalloutNeutral onClick={() => this.navigateFromNotification('/account/follow-requests')}>
                <span>See follow requests</span>
                <AngleRightIcon style={{ width: 12, height: 12, flexShrink: 0, opacity: 0.45 }} />
              </CalloutNeutral>
            )}
            {showLoading ? (
              <EmptyState>
                <LoadingIndicator />
              </EmptyState>
            ) : showNotifications.length === 0 ? (
              <EmptyState>
                <EmptyIconCircle>
                  <BellIcon style={{ width: 22, height: 22, cursor: 'default', opacity: 0.4 }} />
                </EmptyIconCircle>
                <div style={{ fontWeight: 600, fontSize: Constants.SemiSmallFontSize, color: Constants.DarkTextColor }}>
                  You&apos;re all caught up
                </div>
                <Subtitle style={{ margin: 0, maxWidth: '16rem', lineHeight: 1.45, fontSize: Constants.SmallFontSize }}>
                  No notifications to show. Activity on your content and profile will appear here.
                </Subtitle>
              </EmptyState>
            ) : (
              showNotifications.map(({ _id, objectId, type, app, collection, time, user, comment }) => {
                const unread = !!unreadMap[_id];
                const row = type === 'NewComment' && this.getUserAction(unread, null, `${Navigation.content}/${objectId}`, `Your content has new comments: "${app.title.text}"`, time)
                  || type === 'NewCommentMention' && this.getUserAction(unread, null, `${Navigation.content}/${comment.app}`, <>You were mentioned in a comment: &quot;{getFormattedTextElements(comment.text)}&quot;</>, time)
                  || type === 'NewCommentReply' && this.getUserAction(unread, null, `${Navigation.content}/${comment.app}`, <>Your comment has new replies: &quot;{getFormattedTextElements(comment.text)}&quot;</>, time)
                  || type === 'NewCommentLike' && this.getUserAction(unread, null, `${Navigation.content}/${comment.app}`, <>Your comment has new likes: &quot;{getFormattedTextElements(comment.text)}&quot;</>, time)
                  || type === 'NewCollectionComment' && this.getUserAction(unread, null, `${Navigation.collections}/${objectId}`, `Your collection has new comments: "${collection.title}"`, time)
                  || type === 'NewCollectionCommentMention' && this.getUserAction(unread, null, `${Navigation.collections}/${comment.app}`, <>You were mentioned in a comment: &quot;{getFormattedTextElements(comment.text)}&quot;</>, time)
                  || type === 'NewCollectionCommentReply' && this.getUserAction(unread, null, `${Navigation.collections}/${comment.app}`, <>Your comment has new replies: &quot;{getFormattedTextElements(comment.text)}&quot;</>, time)
                  || type === 'NewCollectionCommentLike' && this.getUserAction(unread, null, `${Navigation.collections}/${comment.app}`, <>Your comment has new likes: &quot;{getFormattedTextElements(comment.text)}&quot;</>, time)
                  || type === 'NewAppLike' && this.getUserAction(unread, null, `${Navigation.content}/${app._id}`, `Your content has new likes: "${app.title.text}"`, time)
                  || type === 'NewAppPin' && this.getUserAction(unread, null, `${Navigation.content}/${app._id}`, `Your content has new pins: "${app.title.text}"`, time)
                  || type === 'NewAppEditor' && this.getUserAction(unread, null, `${Navigation.content}/${app._id}`, `You have been added as an editor to the content: "${app.title.text}"`, time)
                  || type === 'NewAppOwner' && this.getUserAction(unread, null, `${Navigation.content}/${app._id}`, `You have been made the owner of the content: "${app.title.text}"`, time)
                  || type === 'NewFollower' && this.getUserAction(unread, user, Navigation.userProfile(user), 'started following you', time)
                  || type === 'NewFollowRequest' && this.getUserAction(unread, user, '/account/follow-requests', 'requested to follow you', time)
                  || type === 'FollowApproved' && this.getUserAction(unread, user, Navigation.userProfile(user), 'accepted your follow request', time)
                  || type === 'NewCollectionSubscriber' && this.getUserAction(unread, null, `/collections/${collection._id}`, `Your collection has new subscribers: "${collection.title}"`, time)
                  || type === 'NewAppCollection' && this.getUserAction(unread, null, `/collections/${collection._id}`, `Your content was added to the collection: "${collection.title}"`, time)
                  || type === 'NewCollectionEditor' && this.getUserAction(unread, null, `/collections/${collection._id}`, `You have been added as an editor to the collection: "${collection.title}"`, time)
                  || type === 'NewCollectionOwner' && this.getUserAction(unread, null, `/collections/${collection._id}`, `You have been made the owner of the collection: "${collection.title}"`, time)
                  || type === 'NewMessage' && this.getUserAction(unread, user, `/?conversation=${objectId}`, 'sent you a new message', time);
                return <React.Fragment key={_id}>{row}</React.Fragment>;
              })
            )}
          </NotificationList>
      </NotificationWrapper>
    );

    return (
      <Popup showCloseButton allowBackgroundClose onClose={this.close}>
        <Card
          onClick={(e) => e.stopPropagation()}
          style={{
            width: 'min(100vw - 2rem, 600px)',
            maxWidth: '100%',
            height: 'min(85vh, 640px)',
            maxHeight: 'min(85vh, 640px)',
            margin: 0,
            overflow: 'hidden',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            padding: 0,
          }}
        >
          <EmbeddedShell>
            <EmbeddedHeader>
              <ModalSheetTitle style={{ margin: 0 }}>Notifications</ModalSheetTitle>
            </EmbeddedHeader>
            <Scrollable
              vertical
              paginationHeight={400}
              onPagination={this.onPagination}
              style={scrollStyle}
              rememberScrollPosition={undefined}
            >
              {listBlock}
            </Scrollable>
          </EmbeddedShell>
        </Card>
      </Popup>
    );
  }
}
